import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  DesignAngularKitModule,
  ItModalComponent,
  ItNotificationService,
  ItTabItemComponent,
  NotificationPosition,
  TimelineElement,
} from 'design-angular-kit';
import { KeycloakService } from 'keycloak-angular';
import { NgxPrintModule } from 'ngx-print';
import { firstValueFrom, forkJoin } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RequestSummaryComponent } from '../../components/request-summary/request-summary.component';
import { Attachment } from '../../models/attachment.dto';
import { FormDto } from '../../models/form.dto';
import { Note } from '../../models/note.dto';
import { DischargeInfo, RequestDto } from '../../models/request.dto';
import { AttachmentService } from '../../services/attachment.service';
import { AuthService } from '../../services/auth.service';
import { NoteService } from '../../services/note.service';
import { RequestService } from '../../services/request.service';
import { AttachCardComponent } from './components/attach-card/attach-card.component';
import { FormRowComponent } from './components/form-row/form-row.component';
import { NewAttachModalComponent } from './components/new-attach-modal/new-attach-modal.component';
import { NewNoteModalComponent } from './components/new-note-modal/new-note-modal.component';
import { NoteCardComponent } from './components/note-card/note-card.component';
import { NotificationCardComponent } from './components/notification-card/notification-card.component';
import { RequestActionComponent } from './components/request-action/request-action.component';
import { BackLinkComponent } from '../../components/back-link/back-link.component';
import { AddModuleComponent } from './components/add-module/add-module.component';
import { DischargeInfoComponent } from './components/discharge-info/discharge-info.component';
import { formPreSaveHandlers } from '../../form-hooks';
import { NewSignedAttachModalComponent } from './components/new-signed-attach-modal/new-signed-attach-modal.component';
import { DischargeInfoActionComponent } from './components/discharge-info-action/discharge-info-action.component';
import { LabelService } from '../../services/label.service';
import { LabelPipe } from '../../pipes/label.pipe';
import { EnrollementInfoComponent } from './components/enrollement-info/enrollement-info.component';
import { EnrollementActionComponent } from './components/enrollment-action/enrollement-action.component';
import { EnrollementData } from '../../models/enrollement-data.model';
import { EnrollementService } from '../../services/enrollement.service';

@Component({
  selector: 'app-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    DesignAngularKitModule,
    FormRowComponent,
    RequestSummaryComponent,
    RequestActionComponent,
    AttachCardComponent,
    NoteCardComponent,
    NgxPrintModule,
    NewNoteModalComponent,
    NewAttachModalComponent,
    NewSignedAttachModalComponent,
    NotificationCardComponent,
    BackLinkComponent,
    AddModuleComponent,
    DischargeInfoComponent,
    EnrollementInfoComponent,
    EnrollementActionComponent,
    DischargeInfoActionComponent,
    LabelPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class DetailComponent {
  @ViewChild('fr') private formRenderer?: ElementRef;
  @ViewChild('printSection', { static: false }) printSection!: ElementRef;
  @ViewChild('noteModal') noteModal!: ItModalComponent;
  @ViewChild('newModuleModal') newModuleModal!: ItModalComponent;
  @ViewChild('attachModal') attachModal!: ItModalComponent;
  @ViewChild('attachSignedModal') attachSignedModal!: ItModalComponent;

  today = new Date();
  notes: Note[] = [];
  attachments: Attachment[] = [];
  notifications: Note[] = [];
  timelineElements: TimelineElement[] = [];
  request: RequestDto | undefined;
  loading: boolean = true;
  forms: FormDto[] | undefined;
  formsToAdd: FormDto[] | undefined;
  uuid: string | undefined;
  selectedForm: FormDto | undefined;
  showFormPage: boolean = false;
  credentials: any;
  ehrServerUrl: string;
  readonly: boolean;
  context = {
    language: 'it',
    theme: 'white',
    readonly: false,
    presentationMode: true,
  };
  formEnvirorment = {
    variables: [] as { name: string; value: any }[],
  };
  formMetaData = {
    name: '',
    version: '',
  };
  compositionId: string | undefined;
  showForm: boolean | undefined;
  isSaving: boolean | undefined;
  openFormIndex: number | undefined;
  formChanged: boolean | undefined;
  printing: boolean = false;
  loadingForm: boolean = false;
  requestDates: DischargeInfo[] = [];
  enrollementData: EnrollementData | undefined;

  constructor(
    private noteService: NoteService,
    private requestService: RequestService,
    private route: ActivatedRoute,
    private keycloakService: KeycloakService,
    private notification: ItNotificationService,
    private cdr: ChangeDetectorRef,
    public authService: AuthService,
    private attachmentService: AttachmentService,
    private router: Router,
    private labelService: LabelService,
    private enrollementService: EnrollementService
  ) {
    this.credentials = {
      authType: 'oauth2',
      token: authService.getTokenParsed(),
    };
    this.ehrServerUrl = environment.ehrServerUrl;
    this.readonly = this.authService.isOperatoreSuperUser;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const uuid = params.get('uuid');
      if (!uuid) {
        console.warn('UUID non trovato nella rotta');
        this.loading = false;
        return;
      }
      this.uuid = uuid;
      this.openFormIndex = history.state?.['openFormIndex'];
      this.loadAllData(uuid);
    });
  }

  private isAuthorizedToModifyOrDelete(formCode: string): boolean {
    const onlyUvmEditable = [
      'Esito valutazione UVM',
      'Scheda di prima valutazione nella rete cure palliative',
      'Modulo rivalutazione Hospice',
    ];
    const noSegnalante = ['PAI'];

    if (
      onlyUvmEditable.includes(formCode) &&
      !(this.authService.isOperatoreUvm || this.authService.isOperatoreUvcp || this.authService.isOperatoreCot)
    ) {
      return false;
    }

    if (
      noSegnalante.includes(formCode) &&
      (this.authService.isOperatoreSegnalante || this.authService.isOperatoreMmg)
    ) {
      return false;
    }

    if (
      !(
        onlyUvmEditable.includes(formCode) || noSegnalante.includes(formCode)
      ) &&
      (this.authService.isOperatoreUvm || this.authService.isOperatoreUvcp)
    ) {
      return false;
    }

    return true;
  }

  modifyForm(event?: FormDto): void {
    if (!this.selectedForm && !event) {
      return;
    } else if (event) {
      this.onFormSelected(event);
    }

    if (!this.isAuthorizedToModifyOrDelete(this.selectedForm!.code)) {
      this.notification.warning(
        'Controllo autorizzazioni.',
        'Il tuo utente non dispone dei permessi per modificare questo modulo.',
        true,
        3000,
        NotificationPosition.Top
      );
      return;
    }

    this.showForm = false;
    this.context.readonly = false;
    this.context.presentationMode = false;
    this.cdr.detectChanges();
    this.showForm = true;
  }

  cancelForm() {
    if (!this.selectedForm) {
      return;
    }
    this.showForm = false;
    this.context.readonly = true;
    this.cdr.detectChanges();
    this.showForm = true;
  }

  loadAllData(uuid: string) {
    this.loading = true;
    this.request = undefined;

    this.requestService.getRequest(uuid).subscribe({
      next: (requestData) => {
        this.request = requestData;
        this.labelService.setSetting(this.request?.setting.code!);
        const ehrId = this.request?.ehrId || this.request?.patient.ehrId;
        this.formEnvirorment.variables = [
          { name: 'ehrId', value: ehrId },
          { name: 'patient', value: this.request?.patient },
          { name: 'protocolCode', value: this.request?.code },
          { name: 'request', value: this.request },
        ];

        forkJoin({
          forms: this.requestService.getForms(uuid),
          formsToAdd: this.requestService.getFormsToAdd(uuid),
          notes: this.noteService.getNotes(uuid),
          attachments: this.attachmentService.findAttachments(this.request!.id),
          requestDates: this.requestService.getDischargeInfo(uuid),
          enrollementData: this.enrollementService.getLast(uuid),
          ownershipInfo: this.requestService.ownershipInfo(uuid),
        }).subscribe({
          next: ({ forms, formsToAdd, notes, attachments, requestDates, enrollementData, ownershipInfo}) => {
            this.forms = this.filterForms(forms);
            this.formsToAdd = this.filterForms(formsToAdd);
            this.notes = notes;
            this.attachments = attachments;
            this.requestDates = requestDates;
            this.enrollementData = enrollementData;
            this.notifications = notes.filter((n) => n.type.notification);
            this.timelineElements = this.createTimelineElements(
              notes.filter((n) => !n.type.insertableByUser)
            );
            if(this.authService.isOperatoreMmg) {
              this.readonly = !ownershipInfo.hasItEverBeenOwnershipOfTheEntity;
              console.log(this.readonly)
            }
            this.loading = false;
            if (this.forms && typeof this.openFormIndex === 'number') {
              this.onFormSelected(this.forms[this.openFormIndex]);
              this.openFormIndex = undefined;
            }
          },
          error: (err) => {
            console.error('Errore durante il recupero di forms o notes:', err);
            this.loading = false;
          },
        });
      },
      error: (err) => {
        console.error('Errore durante il recupero della richiesta:', err);
        this.loading = false;
      },
    });
  }

  reloadAttachments() {
    this.attachmentService.findAttachments(this.request!.id).subscribe({
      next: (result) => {
        this.attachments = result;
      },
    });
  }

  private filterForms(forms: FormDto[]): FormDto[] {
    const hasForm6Definitivo = forms.some(
      (f) => f.formType?.id === 6 && (f.formStatus?.id === 2 || f.formStatus?.id === 4)
    );

    let filteredForms = [...forms];

    const hasPai = filteredForms.some((f) => f.formType?.code === 'PAI');
    const hasUvmNonCompilata = filteredForms.some(
      (f) => f.formType?.code === 'UVM' && !f.compositionId
    );

    if (hasPai && hasUvmNonCompilata) {
      filteredForms = filteredForms.filter((f) => f.formType?.code !== 'PAI');
    }

    if (this.request?.setting.code === 'HCP') {
      filteredForms = filteredForms.filter((form) => {
        if (form.formType?.id === 8 && !hasForm6Definitivo) {
          return false;
        }
        return true;
      });
    }
    if(this.authService.isOperatoreSuperUser) {
      filteredForms = filteredForms.filter(form => form.formStatus?.name)
    }
    return filteredForms;
  }

  cancelledNewNote() {
    this.noteModal.toggle();
  }

  cancelledNewModule() {
    this.newModuleModal.toggle();
  }

  cancelledNewAttach() {
    this.attachModal.toggle();
  }

  cancelledNewSignedAttach() {
    this.attachSignedModal.toggle();
  }

  createTimelineElements(notes: Note[]): TimelineElement[] {
    const timelineElements: TimelineElement[] = [];
    notes.forEach((note) => {
      const formattedDate = note.insertDate
        ? new Date(note.insertDate).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '';

      const element: TimelineElement = {
        title: '',
        text: note.text ? note.text : '',
        signature: `${formattedDate} - ${note.insertUser.name}`,
        pin: {
          text: note.title!,
          type: 'evidence',
        },
      };
      timelineElements.push(element);
    });
    return timelineElements;
  }

  tabSelected(tab: ItTabItemComponent) {
    if (tab.id === 'note') {
      this.loadNotes();
    }
  }

  addedNewNote() {
    this.noteModal.toggle();
    this.loadNotes();
  }

  addedNewAttach() {
    this.attachModal.toggle();
    this.loadAllData(this.uuid!);
  }

  addedNewSignedAttach() {
    this.attachSignedModal.toggle();
    const request = {
      transitionFormId: this.selectedForm!.transitionFormId,
      newStatusId: 4,
      transitionId: this.request!.id,
    };
    this.requestService.changeFormStatus(request).subscribe((result) => {
      this.selectedForm!.formStatus!.code = 'FIRMATO';
      this.loadAllData(this.uuid!);
    });
  }

  addedNewModule(form: Omit<FormDto, 'compositionId'>) {
    this.newModuleModal.toggle();
    this.onFormSelected(form);
  }

  getNoficationNumber(): number {
    return this.notifications.filter((n) => !n.read)?.length | 0;
  }

  loadNotes() {
    if (!this.uuid) return;

    this.noteService.getNotes(this.uuid).subscribe({
      next: (notes) => {
        this.notes = notes;
        this.notifications = notes.filter((n) => n.type.notification);
        this.timelineElements = this.createTimelineElements(
          notes.filter((n) => !n.type.insertableByUser)
        );
      },
      error: (err) => {
        console.error('Errore durante il caricamento delle note:', err);
      },
    });
  }

  formRendered(event: Event): void {
    if (!environment.production) {
      console.log('formRendered', event);
    }
    this.formChanged = false;
    this.loadingForm = false;

    const scriptApi = this.formRenderer?.nativeElement.getScriptApi();
    if (!scriptApi || !this.selectedForm) return;

    // scriptApi.addPreSaveHook(
    //   'preSaveCustomHook',
    //   async () => {
    //     const handler =
    //       formPreSaveHandlers[this.selectedForm!.code] ||
    //       formPreSaveHandlers['*'];
    //     await handler(scriptApi, this.request!, this.requestDates, {
    //       requestService: this.requestService,
    //       authService: this.authService,
    //     });
    //   },
    //   { catchError: false }
    // );

  }

  valueChange(event: any): void {
    if (!environment.production) {
      console.log('valueChange', event.detail);
    }
    this.formChanged = true;
  }

  async onFormSelected($event: FormDto) {
    this.selectedForm = $event;
    this.loadingForm = true;
    this.showFormPage = true;
    this.formMetaData.name = this.selectedForm.code;
    this.formMetaData.version = this.selectedForm.version;
    this.compositionId = this.selectedForm?.compositionId ?? undefined;
    const token = await this.keycloakService.getToken();
    this.credentials.token = token;
    if (this.isAuthorizedToModifyOrDelete(this.selectedForm.code)) {
      this.context.readonly = this.selectedForm.compositionId ? true : false;
    } else {
      this.context.readonly = true;
    }
    this.context.presentationMode = false;
    this.showForm = true;
  }

  async selectFormWithoutOpening($event: FormDto) {
    this.selectedForm = $event;
    this.showFormPage = false;
    this.formMetaData.name = this.selectedForm.code;
    this.formMetaData.version = this.selectedForm.version;
    this.compositionId = this.selectedForm?.compositionId ?? undefined;
    const token = await this.keycloakService.getToken();
    this.credentials.token = token;
    if (this.isAuthorizedToModifyOrDelete(this.selectedForm.code)) {
      this.context.readonly = this.selectedForm.compositionId ? true : false;
    } else {
      this.context.readonly = true;
    }
    this.showForm = false;
  }

  async printForm($event: FormDto) {
    this.selectFormWithoutOpening($event);
    if ($event.formStatus?.code === 'FIRMATO') {
      this.printAttachment($event);
    } else {
      this.goToPrintWindow();
    }
  }

  uploadAttachment(event?: FormDto) {
    if (event) {
      this.selectFormWithoutOpening(event);
    }
    if (!this.isAuthorizedToModifyOrDelete(this.selectedForm!.code)) {
      this.notification.warning(
        'Controllo autorizzazioni.',
        'Il tuo utente non dispone dei permessi per cancellare questo modulo.',
        true,
        3000,
        NotificationPosition.Top
      );
      return;
    } else {
      this.attachSignedModal.toggle();
    }
  }

  downloadAttachment(event: FormDto) {
    this.attachmentService
      .getLastSignedAttachment(event!.compositionId!)
      .subscribe((attachment) => {
        this.attachmentService.downloadAttachment(attachment.url).subscribe({
          next: (blob: Blob) => {
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            a.download = attachment.name || 'download';
            a.click();
            URL.revokeObjectURL(objectUrl);
          },
          error: (err) => {
            console.error('Errore nel download dell’allegato', err);
          },
        });
      });
  }

  private getMimeTypeFromFilename(filename: string): string {
    if (filename.endsWith('.pdf')) return 'application/pdf';
    if (filename.endsWith('.png')) return 'image/png';
    if (filename.endsWith('.jpg') || filename.endsWith('.jpeg'))
      return 'image/jpeg';
    return 'application/octet-stream';
  }

  printAttachment(event: FormDto) {
    this.attachmentService
      .getLastSignedAttachment(event!.compositionId!)
      .subscribe((attachment) => {
        this.attachmentService.downloadAttachment(attachment.url).subscribe({
          next: (blob: Blob) => {
            const mimeType = this.getMimeTypeFromFilename(attachment.name);
            const fileBlob = new Blob([blob], { type: mimeType });

            const objectUrl = URL.createObjectURL(fileBlob);
            const printWindow = window.open(objectUrl, '_blank');

            if (printWindow) {
              printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
              };
            } else {
              console.error('Impossibile aprire la finestra di stampa');
            }
          },
          error: (err) => {
            console.error(
              'Errore nel download per la stampa dell’allegato',
              err
            );
          },
        });
      });
  }

  async save(draft: boolean = true): Promise<void> {
    if (this.isSaving) return;

    this.isSaving = true;

    try {
      const scriptApi = this.formRenderer?.nativeElement.getScriptApi();
      if (!scriptApi) {
        this.notification.error("Impossibile accedere all'API del form.");
        return;
      }

      const response = await scriptApi.saveComposition();

      if (!response.success) {
        this.notification.error(
          'Si è verificato un problema durante il salvataggio dei dati.'
        );
        return;
      }

      const compositionId = response.uid?.split('::')[0];
      let noteRequest: any;
      if (!this.selectedForm?.compositionId) {
        const saveRequest = {
          transitionId: this.request!.id,
          formId: this.selectedForm?.id,
          compositionId,
          formStatusId: draft ? 1 : 2,
        };

        await firstValueFrom(this.requestService.saveForm(saveRequest));

        if (!draft) {
          await this.tryUpdateStatusAfterSave(this.selectedForm!.code);
        }
        noteRequest = {
          transitionId: this.request!.id,
          typeId: '5',
          text: 'Inserito modulo ' + this.selectedForm?.name,
          insertUserCode: this.authService.userInfo.username,
          title: 'Inserimento modulo',
        };
      } else {
        if (!draft) {
          await this.completeFormAsync(this.selectedForm);
        }
        noteRequest = {
          transitionId: this.request!.id,
          typeId: '5',
          text: 'Modificato modulo ' + this.selectedForm?.name,
          insertUserCode: this.authService.userInfo.username,
          title: 'Aggiornamento modulo',
        };
      }
      await firstValueFrom(this.noteService.insertNote(noteRequest));

      const handler =
        formPreSaveHandlers[this.selectedForm!.code] ||
        formPreSaveHandlers['*'];
      await handler(scriptApi, this.request!, this.requestDates, {
        requestService: this.requestService,
        authService: this.authService,
        attachmentService: this.attachmentService
      },this.selectedForm, compositionId);


      this.notification.success(
        'Dati salvati con successo.',
        'Il modulo è stato salvato correttamente.',
        true,
        3000,
        NotificationPosition.Top
      );
      this.reset();
    } catch (error) {
      console.error(error);
      this.notification.error('Errore inatteso durante il salvataggio.');
    } finally {
      this.isSaving = false;
    }
  }

  private async tryUpdateStatusAfterSave(formCode: string): Promise<void> {
    const settingCode = this.request?.setting.code;
    const statusCode = this.request?.status.code;

    if (
      settingCode === 'HCP' &&
      statusCode === 'APE' &&
      (formCode === 'Scheda cure palliative hospice' ||
        formCode ===
          "Scheda Anamnestica per l'accesso al sistema delle cure intermedie, riabilitative e residenziali non ospedaliere")
    ) {
      await firstValueFrom(
        this.requestService.changeStatus(this.request!.uuid, 'DAT')
      );
    } else if (
      settingCode === 'HCP' &&
      statusCode === 'TPV' &&
      formCode === 'Scheda di prima valutazione nella rete cure palliative'
    ) {
      await firstValueFrom(
        this.requestService.changeStatus(this.request!.uuid, 'VAL')
      );
    } else if (
      (settingCode === '26' ||
        settingCode === 'ADI' ||
        settingCode === 'RSA') &&
      statusCode === 'APE' &&
      (formCode === 'Scheda Anamnestica' ||
        formCode ===
          "Scheda Anamnestica per l'accesso al sistema delle cure intermedie, riabilitative e residenziali non ospedaliere")
    ) {
      await firstValueFrom(
        this.requestService.changeStatus(this.request!.uuid, 'DAT')
      );
    } else if (
      settingCode === 'PA' &&
      statusCode === 'APE' &&
      (formCode === 'Modulo segnalazione post acuzie' ||
        formCode ===
          "Scheda Anamnestica per l'accesso al sistema delle cure intermedie, riabilitative e residenziali non ospedaliere")
    ) {
      await firstValueFrom(
        this.requestService.changeStatus(this.request!.uuid, 'DAT')
      );
    } else if (
      settingCode === 'ODC' &&
      statusCode === 'APE' &&
      formCode === 'proposta per ammissione in ospedale di comunita'
    ) {
      await firstValueFrom(
        this.requestService.changeStatus(this.request!.uuid, 'DAT')
      );
    } else if (
      (settingCode === '26' ||
        settingCode === 'ADI' ||
        settingCode === 'RSA') &&
      statusCode === 'TPV' &&
      formCode === 'Esito valutazione UVM'
    ) {
      await firstValueFrom(
        this.requestService.changeStatus(this.request!.uuid, 'VAL')
      );
    }
  }

  private async completeFormAsync(form: FormDto): Promise<void> {
    const request = {
      transitionFormId: form.transitionFormId,
      newStatusId: 2,
      transitionId: this.request!.id,
    };
    await firstValueFrom(this.requestService.changeFormStatus(request));
    await this.tryUpdateStatusAfterSave(form.code);
  }

  reset(): void {
    this.selectedForm = undefined;
    this.showFormPage = false;
    this.loadAllData(this.uuid!);
  }

  defaultDateFormat: string = 'dd/MM/yyyy';

  formError(error: any) {
    console.log(error);
    console.log(error.detail.description);
    console.log(error.detail.type);

    this.notification.error(
      'Si è verificato un problema durante il caricamento del modulo.'
    );
    this.reset();
  }

  completeForm() {
    const request = {
      transitionFormId: this.selectedForm!.transitionFormId,
      newStatusId: 2,
      transitionId: this.request!.id,
    };
    const selectedForm = this.selectedForm;
    this.requestService.changeFormStatus(request).subscribe((response) => {
      if (
        this.request!.setting.code === 'HCP' &&
        this.request!.status.code === 'APE' &&
        selectedForm?.code === 'Scheda cure palliative hospice'
      ) {
        this.requestService
          .changeStatus(this.request!.uuid, 'DAT')
          .subscribe((response) => {
            this.reset();
          });
      } else if (
        this.request!.setting.code === 'HCP' &&
        this.request!.status.code === 'TPV' &&
        selectedForm?.code ==
          'Scheda di prima valutazione nella rete cure palliative'
      ) {
        this.requestService
          .changeStatus(this.request!.uuid, 'VAL')
          .subscribe((response) => {
            this.reset();
          });
      } else if (
        (this.request!.setting.code === '26' ||
          this.request!.setting.code === 'ADI' ||
          this.request!.setting.code === 'RSA') &&
        this.request!.status.code === 'APE' &&
        (selectedForm?.code === 'Scheda Anamnestica' ||
          selectedForm?.code ===
            "Scheda Anamnestica per l'accesso al sistema delle cure intermedie, riabilitative e residenziali non ospedaliere")
      ) {
        this.requestService
          .changeStatus(this.request!.uuid, 'DAT')
          .subscribe((response) => {
            this.reset();
          });
      } else if (
        this.request!.setting.code === 'PA' &&
        this.request!.status.code === 'APE' &&
        selectedForm?.code === 'Modulo segnalazione post acuzie'
      ) {
        this.requestService
          .changeStatus(this.request!.uuid, 'DAT')
          .subscribe((response) => {
            this.reset();
          });
      } else if (
        this.request!.setting.code === 'ODC' &&
        this.request!.status.code === 'APE' &&
        selectedForm?.code === 'proposta per ammissione in ospedale di comunita'
      ) {
        this.requestService
          .changeStatus(this.request!.uuid, 'DAT')
          .subscribe((response) => {
            this.reset();
          });
      }else if (
        (this.request!.setting.code === '26' ||
          this.request!.setting.code === 'ADI' ||
          this.request!.setting.code === 'RSA') &&
        this.request!.status.code === 'TPV' &&
        selectedForm?.code === 'Esito valutazione UVM'
      ) {
        this.requestService
          .changeStatus(this.request!.uuid, 'VAL')
          .subscribe((response) => {
            this.reset();
          });
      } else {
        this.reset();
      }
    });
  }

  deleteForm(event?: FormDto) {
    if (!this.selectedForm && !event) {
      return;
    } else if (event) {
      this.selectFormWithoutOpening(event);
    }
    if (!this.isAuthorizedToModifyOrDelete(this.selectedForm!.code)) {
      this.notification.warning(
        'Controllo autorizzazioni.',
        'Il tuo utente non dispone dei permessi per cancellare questo modulo.',
        true,
        3000,
        NotificationPosition.Top
      );
      return;
    }

    const request = {
      transitionFormId: this.selectedForm!.transitionFormId,
      newStatusId: 3,
      transitionId: this.request!.id,
    };

    this.requestService.changeFormStatus(request).subscribe((response) => {
      this.reset();
    });
  }

  goToPrintWindow() {
    if(this.selectedForm!.printCode) {
      this.requestService.print(this.selectedForm!.printCode, this.request?.uuid, this.selectedForm?.compositionId).subscribe((result) => {
        const mimeType = 'application/pdf';
            const fileBlob = new Blob([result], { type: mimeType });

            const objectUrl = URL.createObjectURL(fileBlob);
            const printWindow = window.open(objectUrl, '_blank');

            if (printWindow) {
              printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
              };
        }}
      );
    } else {
      const state = {
        formMetaData: this.formMetaData,
        compositionId: this.selectedForm?.compositionId,
        credentials: this.credentials,
        ehrServerUrl: this.ehrServerUrl,
        environment: this.formEnvirorment,
        context: {
          ...this.context,
          ehrId: this.request?.ehrId || this.request?.patient?.ehrId,
      },
      returnUrl: this.router.url,
      request: this.request,
      };

      this.router.navigateByUrl('/print', { state });
    }
  }

  hasConsentAttachment(): boolean {
    return this.attachments.some(att => att?.type?.code === 'CON');
  }
}
